import {BaseWebComponentView} from '../../../framework/BaseWebComponentView';
import {
  ACTION_TAKEN,
  ColumnIndexes,
  ColumnKeys,
  COMPLEX_SEARCH_FILTERS,
  computeTimeOptionsFromEdrStorageTimeRange,
  ExtraParametersSchema,
  INCIDENT_TYPE,
  IncidentData,
  KILL_CHAIN_PHASES,
  PRIORITY,
  STATUS,
} from '../types';
import {DetectedIncidentsTemplatesStringKeys} from '@nirvana/shared/dtos/types/widgets/common';
import {
  WEB_COMPONENT_GENERIC_GRID_SMART_VIEWS,
  WEB_COMPONENT_GRID_VIEW,
  WEB_COMPONENT_GZ_ICON_LABEL,
  WEB_COMPONENT_GZ_PUSH_BODY_LAYOUT,
} from '../../../webcomponents.constants';
import {GridViewWebComponent} from '../../../grid/gridView';
import {IncidentsGridDataProvider} from '../dataProvider/IncidentsGridDataProvider';
import {ColumnType} from '../../../grid/types/common';
import {GridTitleConfig} from '../../../grid/uiElements/gridViewTitle/types';
import moment from 'moment/moment';

import {SmartViewInlineActions} from '../../../grid/gridSmartViews/types';
import {RequiredValidator} from '../../../ui/gzForm/validators/required.validator';
import {GenericGridSmartViewsWebComponent} from '../../../grid/genericGridSmartViews';
import {Column} from '@nirvana/shared/dtos/types/shared/filters';
import {GridSmartViewsWebComponent} from '../../../grid/gridSmartViews';
import {GzPushBodyLayoutWebComponent} from '../../../ui/gzPushBodyLayout';
import {GzSideMenuWebComponent} from '../../../ui/gzSideMenu';
import {
  CategoriesConfig,
  convertGenericViewConfigToGridViewConfig,
  ValidateViewNameErrorCodeResponse,
  ValidateViewNameResponse,
} from '../../../grid/genericGridSmartViews/types';
import {RemoteValidator} from '../../../ui/gzForm/validators/remote.validator';
import {TemplateState} from '../../../types/common/types';
import {TimeRangeKind} from '@nirvana/shared/dtos/types/shared/time';
import {
  actorsRenderer,
  assigneeRenderer,
  companyRenderer,
  correlatedRenderer,
  createdRenderer,
  entitiesRenderer,
  idRenderer,
  lastKillChainPhaseRenderer,
  lastUpdatedRenderer,
  phasrCategoriesRenderer,
  resourcesRenderer,
  scoreRenderer,
  selectColumnRenderer,
} from './CustomGridRenderers';

import {IncidentsSideMenuWebComponent} from '../../incidentsSideMenu';
import {InputWidthValues} from '../../../ui/gzInput';
import {processEntitiesRowData, processResourcesRowData} from '../../../utils/xdr.utils';
import {PanelType} from '../../incidentsSideMenu/types';
import _ from 'lodash';
import {GeneralSectionLabels, XdrIncidentDetailsWebComponent} from '../../xdrIncidentDetails';
import {EdrIncidentDetailsWebComponent} from '../../edrIncidentDetails';
import {
  AnalysisSectionLabels,
  AttackInfoSectionLabels,
  DetectionsSectionLabels,
  TechniquesSectionLabels,
} from '../../edrIncidentDetails/types';
import {Feature, Rights} from '@nirvana/shared/constants';
import {ElementClass, toggleActive} from '../../../utils/htmlElement.utils';
import {DefaultGridColumnFixedWidths, GridColumnConfiguration} from '@nirvana/shared/dtos/types/shared/grid';
import {translateFn} from '../../../utils/translator.helper';
import {RemoteDetailsTypes} from '../../../filters/backendEnumFilter/types';
import {GenericGridSmartViewsHelpers} from '../../../grid/genericGridSmartViews/helpers/GenericGridSmartViewsHelpers';
import {ConsoleRequest, DataProviderType} from "../../../utils/request";
import {SelectionStoreMode} from "../../../grid/gridView/types";

const CUSTOM_COLUMN_WIDTH_CONFIGS = {
  [ColumnKeys.NUMBER]: {
    customWidth: DefaultGridColumnFixedWidths.S,
  },
};

export class IncidentsGridView extends BaseWebComponentView {
  public allColumns;
  public smartViewsStrings;
  public smartViewSettings;
  public incidentsSideMenuStrings;
  public incidentsDetailsSidePanelString;
  public availableAssignees;
  public assigneesOptions;

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('gz-push-body-layout .wrapper');
    this.dom.$title = this.shadow.querySelector('gz-push-body-layout .title');
    this.dom.$pageTitle = this.shadow.querySelector('.page__header .title');
    this.dom.$loader = this.shadow.querySelector('gz-push-body-layout .loader');
    this.dom.$grid = this.shadow.querySelector(
      `gz-push-body-layout ${WEB_COMPONENT_GRID_VIEW}`,
    ) as GridViewWebComponent<Column, ColumnKeys>;
    this.dom.$loadingErrorMessage = this.shadow.querySelector('gz-push-body-layout .loading-error--message');
    this.dom.$gridSmartViews = this.shadow.querySelector(
      `gz-push-body-layout ${WEB_COMPONENT_GENERIC_GRID_SMART_VIEWS}`,
    ) as GridSmartViewsWebComponent;
    this.dom.$smartViewsTitle = this.shadow.querySelector('gz-push-body-layout #smartViewsTitle');
    this.dom.$pushBodyLayout = this.shadow.querySelector(
      WEB_COMPONENT_GZ_PUSH_BODY_LAYOUT,
    ) as GzPushBodyLayoutWebComponent;
    this.dom.$pushBodySideNav = this.shadow.querySelector('[slot="push-body-sidenav"]');

    this.dom.$sideMenu = this.shadow.querySelector('gz-push-body-layout #sideMenu') as GzSideMenuWebComponent;
    this.dom.changeStatusBulk = {
      $button: this.shadow.querySelector('#change-status-bulk-button'),
      $text: this.shadow.querySelector('#change-status-bulk-button-text'),
    };
    this.dom.prioritizeBulk = {
      $button: this.shadow.querySelector('#prioritize-bulk-button'),
      $text: this.shadow.querySelector('#prioritize-bulk-button-text'),
    };
    this.dom.$assignBulkButton = this.shadow.querySelector('#assign-bulk-button');
    this.dom.$header = this.shadow.querySelector('[slot=grid-header-actions-slot]');
    this.dom.$demoModeButton = this.shadow.querySelector('#demo-mode-button');
    this.dom.$demoModeButtonContainer = this.shadow.querySelector('.demo-mode-container');

    this.dom.$incidentsSideMenu = this.shadow.querySelector(
      'gz-push-body-layout #incidentsSideMenu',
    ) as IncidentsSideMenuWebComponent;

    this.setTranslatedStrings();
  }

  getTranslations() {
    return {
      commonStrings: this.commonStrings,
      smartViewsStrings: this.smartViewsStrings,
      incidentsSideMenuStrings: this.incidentsSideMenuStrings,
      defaultViewsTranslations: {
        all_incidents: this.dependencies.translateFn('All incidents'),
        assigned_to_you: this.dependencies.translateFn('Assigned to you'),
        endpoint_incidents: translateFn('Endpoint incidents'),
        organization_incidents: translateFn('Organization incidents'),
      },
    };
  }

  setTranslatedStrings() {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
    this.smartViewsStrings = this.stringsTranslator.getSmartViewsTranslations();
    this.dom.$loadingErrorMessage.innerHTML = this.commonStrings.generalSystemError;
    this.dom.$smartViewsTitle.innerHTML = this.smartViewsStrings.genericPanelTitle.toUpperCase();
    this.incidentsSideMenuStrings = this.stringsTranslator.getIncidentsSideMenuStrings();
    this.incidentsDetailsSidePanelString = this.stringsTranslator.getIncidentsDetailsSidePanelString();
    this.dom.changeStatusBulk.$text.innerText = this.dependencies.translateFn('Change status');
    this.dom.prioritizeBulk.$text.innerText = this.dependencies.translateFn('Prioritize');
    this.dom.$assignBulkButton.btnText = this.dependencies.translateFn('Assign');
    this.dom.$pageTitle.innerHTML = this.dependencies.translateFn('Incidents');
  }

  setResourcesStringTranslate(type: number): string {
    return Translator.translateXdrResourceType(type);
  }

  setEntityStringTranslate(type: number): string {
    return Translator.translateXdrEntityType(type);
  }

  setTimezone(data) {
    return moment(data).format('DD MMM YYYY, HH:mm');
  }

  private async buildActionButtons() {
    this.removeElementClass(this.dom.$header, ElementClass.HIDDEN);
    this.dom.changeStatusBulk.$button.options = Object.values(STATUS)
      .filter(value => !isNaN(Number(value)))
      .map(value => {
        return {
          title: Translator.translateEdrIncidentStatus(value),
          value,
        };
      });
    this.dom.prioritizeBulk.$button.options = [
      {
        title: translateFn('Unassigned'),
        value: PRIORITY.UNASSIGNED,
      },
      {
        title: translateFn('Critical'),
        value: PRIORITY.CRITICAL,
      },
      {
        title: translateFn('High'),
        value: PRIORITY.HIGH,
      },
      {
        title: translateFn('Medium'),
        value: PRIORITY.MEDIUM,
      },
      {
        title: translateFn('Low'),
        value: PRIORITY.LOW,
      },
    ];

    this.availableAssignees = await ConsoleRequest.createRequest<any>({
      dataProviderType: DataProviderType.CORE,
      endpoint: 'UnifiedIncidents',
      method: 'getAvailableUsersForAssigningIncident',
      payload: {},
    });

    this.assigneesOptions = [{
      label: translateFn('Unassigned'),
      value: '',
    }];

    this.assigneesOptions = this.assigneesOptions.concat(this.availableAssignees.map(assignee => {
      return {
        label: assignee.profile.fullName,
        value: assignee._id,
      };
    }));

    this.dom.$assignBulkButton.filterOptions = this.assigneesOptions;
  }

  private async showDemoBtn() {
    if (
      !Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_EDR_INCIDENTS]) ||
      !Application.Main.product.isCloud() ||
      Application.Main.product.isPrivateCloud()
    ) {
      return;
    }

    this.removeElementClass(this.dom.$demoModeButtonContainer, ElementClass.HIDDEN);
    const isDemoIncidentAvailable =
      await this.dependencies.api.clients.UnifiedIncidents.isDemoIncidentAvailable().toPromise();

    if (!isDemoIncidentAvailable) {
      toggleActive(this.dom.$demoModeButton, false);
      this.dom.$demoModeButton.tooltipText = translateFn(
        'Demo incident unavailable. Please contact Technical Support.',
      );
      this.dom.$demoModeButton.disabled = true;
      return;
    }

    const xdrDemoModeStatus = Application.Main.getUser()['xdrDemoModeEnabled'];
    this.dom.$demoModeButton.disabled = false;
    toggleActive(this.dom.$demoModeButton, xdrDemoModeStatus);
    this.dom.$demoModeButton.tooltipText = xdrDemoModeStatus
      ? translateFn('Hide demo incident')
      : translateFn('Show demo incident');
  }

  buildSuccessView(data: ExtraParametersSchema) {
    this.allColumns = this.getColumns(data.incidentsData.availableUsers);

    this.buildSmartViews().then(async () => {
      if (data.externalFilters.length > 0) {
        await this.dom.$gridSmartViews.controller.setDefaultViewAsActiveView();
      }

      const activeView = this.dom.$gridSmartViews.controller.getActiveView();
      this.smartViewSettings = this.dom.$gridSmartViews.controller.getViewState();
      if (this.dom.$pushBodyLayout.isOpen === this.smartViewSettings.isCollapsed) {
        this.dom.$pushBodyLayout.toggleSideNav();
      }
      if (this.smartViewSettings.panelSize) {
        this.dom.$pushBodyLayout.style.setProperty('--expanded-width', this.smartViewSettings.panelSize + 'px');
      }

      await this.buildGrid(this.allColumns, convertGenericViewConfigToGridViewConfig(activeView), data.externalFilters);
      await this.buildActionButtons();
      await this.showDemoBtn();
    });
  }

  buildErrorView() {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  private async buildSmartViews() {
    if (!(this.dom.$gridSmartViews instanceof GenericGridSmartViewsWebComponent)) {
      throw new Error('Missing grid smart views');
    }
    this.dom.$gridSmartViews.categoriesConfig = this.getSmartViewsCategories();
    this.dom.$gridSmartViews.hasSearchInput = true;
    this.dom.$gridSmartViews.isEditedFn = this.isEdited.bind(this);
    this.dom.$gridSmartViews.injectDependencies(this.dependencies);

    await this.dom.$gridSmartViews.start();
    this.setElementClass(this.dom.$loader, 'hidden');
    this.removeElementClass(this.dom.$gridSmartViews, 'hidden');
  }

  isEdited() {
    return false;
  }

  private getSmartViewsCategories(): CategoriesConfig {
    const {defaultViewsTranslations} = this.getTranslations();
    return {
      internalCategories: {
        recent: {
          isAvailable: false,
        },
        favorites: {
          isAvailable: true,
        },
        default: {
          isAvailable: true,
          translations: defaultViewsTranslations,
        },
      },
      customCategories: [
        {
          sectionTitle: this.dependencies.translateFn('SAVED'),
          type: 'saved',
          inlineActions: [
            {
              title: 'Rename',
              value: SmartViewInlineActions.RENAME,
            },
            {
              title: 'Delete',
              value: SmartViewInlineActions.DELETE,
              customClass: 'danger',
            },
          ],
          emptyStateItemText: this.dependencies.translateFn('No saved views yet'),
          smartViewMaxInputLength: 64,
          noSpaceEscape: true,
          smartViewValidators: [
            RequiredValidator('This field is required.'),
            RemoteValidator(
              (value: string) => {
                const viewName = value.trimEnd().trimStart();

                return this.dependencies.api.clients.SmartViews.validateViewName(this.dependencies.componentName, {
                  name: viewName,
                });
              },
              (response: ValidateViewNameResponse) => response.valid,
              (response: ValidateViewNameResponse) => this.translateValidateViewNameErrorCode(response.errorCode),
              this.dependencies.translateFn('A communication error has occurred. Please try again later.'),
            ),
          ],
          items: [],
        },
      ],
    };
  }

  translateValidateViewNameErrorCode(errorCode: number) {
    if (errorCode === ValidateViewNameErrorCodeResponse.INVALID_VIEWNAME_LENGTH_CODE) {
      return this.dependencies.translateFn('A view name must have a maximum of 1000 characters.');
    }
    if (errorCode === ValidateViewNameErrorCodeResponse.INVALID_VIEWNAME_DUPLICATE_NAME_CODE) {
      return this.dependencies.translateFn('A view with this name already exists.');
    }
    if (errorCode === ValidateViewNameErrorCodeResponse.INVALID_VIEWNAME_INVALID_CHARACTERS) {
      return this.dependencies.translateFn('A view name must not contain special characters.');
    }
  }

  async buildGrid(allColumns, view, externalFilters: Array<any> = []) {
    const defaultFilters: any = this.getDefaultFilters();
    const predefinedFilters = defaultFilters.map(f => Object.keys(f)[0]);

    let initialViewWithoutExternalFilters;
    if (externalFilters.length > 0) {
      initialViewWithoutExternalFilters = _.cloneDeep(view);

      for (const externalFilter of externalFilters) {
        const filterName = Object.keys(externalFilter)[0];

        const filterIndex = defaultFilters.findIndex(elem => {
          return Object.keys(elem).includes(filterName);
        });

        if (filterIndex === -1) {
          defaultFilters.push(externalFilter);
        } else {
          defaultFilters[filterIndex] = externalFilter;
        }
      }

      view.filters = [...defaultFilters];
    }

    //only for predefined views and only for mdrSuperAnalyst overwrite company filter
    if (this.dependencies.getUser().mdrSuperAnalyst && view.isReadOnly === true) {
      view.filters = view.filters.map(filter => {
        if (filter.hasOwnProperty(ColumnKeys.COMPANY)) {
          filter[ColumnKeys.COMPANY].in = [this.dependencies.Ext.getActiveCompany().companyId];
        }
        return filter;
      });
    }

    this.updateFiltersBasedOnAvailability(view);
    this.updateColumnsOrderBasedOnAvailability(view);

    const columnRenderers = {
      [ColumnKeys.NUMBER]: idRenderer.bind(this),
      [ColumnKeys.CREATED]: createdRenderer.bind(this),
      [ColumnKeys.LAST_UPDATED]: lastUpdatedRenderer.bind(this),
      [ColumnKeys.ASSIGNEE]: assigneeRenderer.bind(this),
      [ColumnKeys.COMPANY]: companyRenderer.bind(this),
      [ColumnKeys.ENTITIES]: entitiesRenderer.bind(this),
      [ColumnKeys.RESOURCES]: resourcesRenderer.bind(this),
      [ColumnKeys.LAST_KILLCHAIN_PHASE]: lastKillChainPhaseRenderer.bind(this),
      [ColumnKeys.ACTORS]: actorsRenderer.bind(this),
      [ColumnKeys.SCORE]: scoreRenderer,
      [ColumnKeys.CORRELATED]: correlatedRenderer.bind(this),
      [ColumnKeys.SELECT]: selectColumnRenderer.bind(this),
      [ColumnKeys.PHASR_CATEGORIES]: phasrCategoriesRenderer.bind(this),
    };
    //view data needs to come from API should be converted to the format that the current grid system supports

    this.dom.$grid.propertiesSet = false;
    this.dom.$grid.hasFilters = true;
    this.dom.$grid.dataProvider = new IncidentsGridDataProvider(this.dependencies);
    this.dom.$grid.allColumnDetails = allColumns;
    this.dom.$grid.enableRowClickEvent = true;
    this.dom.$grid.columnRenderers = columnRenderers;
    this.dom.$grid.predefinedFiltersKeys = predefinedFilters;
    this.setGridViewProps(view);
    this.dom.$grid.sideMenuParent = this.dom.$wrapper;
    this.dom.$grid.hasSelectableRows = true;
    this.dom.$grid.hasPagination = true;
    this.dom.$grid.pageSizeOptions = [
      {
        title: '10',
        value: '10',
      },
      {
        title: '20',
        value: '20',
      },
      {
        title: '30',
        value: '30',
      },
      {
        title: '50',
        value: '50',
      },
      {
        title: '100',
        value: '100',
      },
    ];
    this.dom.$grid.selectionStoreConfig = {
      functionalMode: SelectionStoreMode.RECORDS,
    };

    this.dom.$grid.smartViewsEnabled = true;
    this.dom.$grid.activeViewProps = view;

    if (externalFilters.length > 0) {
      this.dom.$grid.defaultViewProps = initialViewWithoutExternalFilters;
      this.dom.$grid.titleConfig = new GridTitleConfig(this.getViewTitle(view) + '*');
      this.dom.$grid.activeViewInitialProps = _.cloneDeep(initialViewWithoutExternalFilters);
      this.dom.$grid.smartViewDiscardButtonDisabledState = false;
    } else {
      this.dom.$grid.defaultViewProps = _.cloneDeep(view);
      this.dom.$grid.titleConfig = new GridTitleConfig(this.getViewTitle(view));
      this.dom.$grid.activeViewInitialProps = _.cloneDeep(view);
      this.dom.$grid.smartViewDiscardButtonDisabledState = true;
    }

    this.dom.$grid.enableCSVExport = true;
    this.dom.$grid.filtersUiConfigs = [
      {
        key: ColumnKeys.CREATED,
        config: {
          additionalTimeRangeOptions: this.getTimeOptionsForEdrStorageAddon(),
          enableSelectAllToDate: true,
        },
      },
      {
        key: ColumnKeys.LAST_UPDATED,
        config: {
          additionalTimeRangeOptions: this.getTimeOptionsForEdrStorageAddon(),
          enableSelectAllToDate: true,
        },
      },
      {
        key: ColumnKeys.CORRELATED,
        config: {
          inputWidth: InputWidthValues.LARGE,
        },
      },
    ];

    this.dom.$grid.propertiesSet = true;
    this.dom.$sideMenu.closeSideMenu();

    if (!this.dom.$grid.started) {
      this.dom.$grid.injectDependencies(this.dependencies);
      await this.dom.$grid.start();
    }
  }

  getViewTitle(view) {
    return view.label;
  }

  /**
   * Filters will be "filtered" as follows:
   * - if column is not available in the grid, it will be removed from filters
   *
   * @param view
   */
  updateFiltersBasedOnAvailability(view) {
    const columnKeyMapping = {};
    for (const column of this.allColumns) {
      columnKeyMapping[column.key] = true;
    }

    // Keep only the filters that have a corespondent column in the grid
    view.filters = view.filters.filter(filter => {
      const columnKeys = Object.keys(filter);

      if (columnKeyMapping[columnKeys[0]]) {
        return true;
      }

      return false;
    });
  }

  /**
   * Get only available columns based on features flags and platform
   */
  updateColumnsOrderBasedOnAvailability(view) {
    // For on premise, remove some filters
    if (!Application.Main.product.isCloud()) {
      view.displayedColumns = view.displayedColumns.filter(column => {
        if (
          [
            ColumnIndexes.COMPANY,
            ColumnIndexes.LAST_KILLCHAIN_PHASE,
            ColumnIndexes.RESOURCES,
            ColumnIndexes.TYPE,
          ].includes(column)
        ) {
          return false;
        }

        return true;
      });
    }

    if (Application.Main.company.isCustomer()) {
      view.displayedColumns = view.displayedColumns.filter(column => {
        if (column === ColumnIndexes.COMPANY) {
          return false;
        }
        return true;
      });
    }

    // For users without EDR or OnPremise, remove specific columns
    if (!Application.Main.hasFeatures(Feature.PRODUCT_FEATURE_EDR_INCIDENTS) || !Application.Main.product.isCloud()) {
      view.displayedColumns = view.displayedColumns.filter(column => {
        if (
          [
            ColumnIndexes.LAST_KILLCHAIN_PHASE,
            ColumnIndexes.RESOURCES,
            ColumnIndexes.TYPE,
          ].includes(column)
        ) {
          return false;
        }

        return true;
      });
    }
  }

  getColumns(availableUsers) {
    const allColumns: any = [
      {
        column: ColumnIndexes.NUMBER,
        key: ColumnKeys.NUMBER,
        columnName: this.dependencies.translateFn('ID'),
        type: ColumnType.NUMBER,
        width: '100px',
        removable: false,
        sortable: false,
        resizable: false,
      },
      {
        column: ColumnIndexes.CREATED,
        key: ColumnKeys.CREATED,
        columnName: this.dependencies.translateFn('Created on'),
        type: ColumnType.DATE,
        width: '100px',
        removable: false,
        sortable: false,
        filterResetValue: {
          timeRange: {
            kind: TimeRangeKind.ALL_TO_DATE,
          },
        },
      },
      {
        column: ColumnIndexes.LAST_UPDATED,
        key: ColumnKeys.LAST_UPDATED,
        columnName: this.dependencies.translateFn('Last updated on'),
        type: ColumnType.DATE,
        removable: false,
        sortable: false,
        width: '100px',
        filterResetValue: {
          timeRange: {
            kind: TimeRangeKind.ALL_TO_DATE,
          },
        },
      },
      {
        column: ColumnIndexes.STATUS,
        key: ColumnKeys.STATUS,
        columnName: this.dependencies.translateFn('Status'),
        type: ColumnType.ENUM,
        values: this.getStatusValues(),
        removable: false,
        sortable: false,
        editingDisabled: false,
        width: '100px',
      },
      {
        column: ColumnIndexes.ASSIGNEE,
        key: ColumnKeys.ASSIGNEE,
        columnName: this.dependencies.translateFn('Assignee'),
        type: ColumnType.SELECT,
        values: availableUsers.map(u => {
          return {
            label: u.name,
            value: u._id,
          };
        }),
        removable: true,
        sortable: false,
        width: '100px',
      },
      {
        column: ColumnIndexes.PRIORITY,
        key: ColumnKeys.PRIORITY,
        columnName: this.dependencies.translateFn('Priority'),
        type: ColumnType.ENUM,
        values: this.getPriorityValues(),
        removable: true,
        sortable: false,
        width: '100px',
      },
      {
        column: ColumnIndexes.SCORE,
        key: ColumnKeys.SCORE,
        columnName: this.dependencies.translateFn('Severity score'),
        type: ColumnType.RANGE,
        removable: false,
        sortable: false,
        width: '100px',
      },
      {
        column: ColumnIndexes.SELECT,
        key: ColumnKeys.SELECT,
        columnName: '',
        type: ColumnType.ACTION,
        removable: false,
        sortable: false,
        width: '100px',
        filteringDisabled: true,
      },
    ];

    if (Application.Main.product.isCloud() && !Application.Main.company.isCustomer()) {
      let defaultCompany = 'all';
      if (this.dependencies.getUser().mdrSuperAnalyst) {
        defaultCompany = this.dependencies.Ext.getActiveCompany().companyId;
      }

      allColumns.push({
        column: ColumnIndexes.COMPANY,
        key: ColumnKeys.COMPANY,
        columnName: this.dependencies.translateFn('Company'),
        type: ColumnType.COMPANY,
        removable: true,
        sortable: false,
        enumFilterCanSelectAll: true,
        showOnlySelectAll: true,
        filterResetValue: [defaultCompany],
        width: '100px',
      });
    }

    allColumns.push({
      column: ColumnIndexes.ENTITIES,
      key: ColumnKeys.ENTITIES,
      columnName: this.dependencies.translateFn('Entities'),
      type: ColumnType.COMPLEX_ENUM_FILTER,
      remoteDetails: {
        type: RemoteDetailsTypes.IncidentsEntities,
      },
      removable: true,
      sortable: false,
      width: '100px',
    });

    // Add columns only for users with EDR
    if (Application.Main.product.isCloud() && Application.Main.hasFeatures(Feature.PRODUCT_FEATURE_EDR_INCIDENTS)) {
      allColumns.push(
        ...[
          {
            column: ColumnIndexes.RESOURCES,
            key: ColumnKeys.RESOURCES,
            columnName: this.dependencies.translateFn('Resources'),
            type: ColumnType.COMPLEX_ENUM_FILTER,
            remoteDetails: {
              type: RemoteDetailsTypes.IncidentsResources,
            },
            removable: true,
            sortable: false,
            width: '100px',
          },
          {
            column: ColumnIndexes.CORRELATED,
            key: ColumnKeys.CORRELATED,
            columnName: this.dependencies.translateFn('Correlated incidents'),
            type: ColumnType.NUMBER,
            removable: true,
            sortable: false,
            width: '100px',
          },
          {
            column: ColumnIndexes.TYPE,
            key: ColumnKeys.TYPE,
            columnName: this.dependencies.translateFn('Incident type'),
            type: ColumnType.ENUM,
            values: this.getTypeValues(),
            removable: true,
            sortable: false,
          },
          {
            column: ColumnIndexes.LAST_KILLCHAIN_PHASE,
            key: ColumnKeys.LAST_KILLCHAIN_PHASE,
            columnName: this.dependencies.translateFn('Last killchain phase'),
            type: ColumnType.ENUM,
            values: this.getKillchainValues(),
            removable: true,
            sortable: false,
            width: '100px',
          },
        ],
      );
    }
    if (this.isActorsColumnVisible()) {
      allColumns.push(
        ...[
          {
            column: ColumnIndexes.ACTORS,
            key: ColumnKeys.ACTORS,
            columnName: this.dependencies.translateFn('Actors'),
            type: ColumnType.COMPLEX_ENUM_FILTER,
            remoteDetails: {
              type: RemoteDetailsTypes.IncidentsActors,
            },
            removable: true,
            sortable: false,
            width: '100px',
          },
        ]);
    }

    if (Application.Main.hasFeatures(Feature.PRODUCT_FEATURE_PHASR_VISIBLE)) {
      allColumns.push(
        ...[
          {
            column: ColumnIndexes.PHASR_CATEGORIES,
            key: ColumnKeys.PHASR_CATEGORIES,
            columnName: this.dependencies.translateFn('Targeted activity type'),
            type: ColumnType.ENUM,
            values: this.getPhasrCategoriesValues(),
            removable: true,
            sortable: false,
            width: '100px',
          },
        ]);
    }

    allColumns.push(
      ...[
        {
          column: ColumnIndexes.ACTION_TAKEN,
          key: ColumnKeys.ACTION_TAKEN,
          columnName: this.dependencies.translateFn('Action taken'),
          type: ColumnType.ENUM,
          values: this.getActionTakenValues(),
          removable: true,
          sortable: false,
          width: '100px',
        },
        {
          column: ColumnIndexes.SEARCH_DATA,
          key: ColumnKeys.SEARCH_DATA,
          columnName: this.dependencies.translateFn('Complex Search'),
          type: ColumnType.COMPLEX_SEARCH,
          values: this.getComplexSearchDropdownValues(),
          removable: false,
          sortable: false,
          width: '100px',
        },
      ],
    );

    return allColumns;
  }

  private isActorsColumnVisible() {
    return Application.Main.product.isCloud() &&
      (
        (Application.Main.company.isCustomer() && Application.Main.hasFeatures(Feature.PRODUCT_FEATURE_EDR_VISIBLE)) ||
        !Application.Main.company.isCustomer()
      );
  }

  private getActionTakenValues() {
    return [
      {
        label: this.dependencies.translateFn('Reported'),
        value: ACTION_TAKEN.REPORTED,
      },
      {
        label: this.dependencies.translateFn('Blocked'),
        value: ACTION_TAKEN.BLOCKED,
      },
      {
        label: this.dependencies.translateFn('Partially blocked'),
        value: ACTION_TAKEN.PARTIALLY_BLOCKED,
      },
    ];
  }

  private getComplexSearchDropdownValues() {
    const filters = [
      {
        label: this.dependencies.translateFn('Alert name'),
        value: COMPLEX_SEARCH_FILTERS.ALERT_NAME,
      },
    ];

    if (Application.Main.hasFeatures(Feature.PRODUCT_FEATURE_EDR_INCIDENTS)) {
      filters.push(
        {
          label: this.dependencies.translateFn('Att&ck technique'),
          value: COMPLEX_SEARCH_FILTERS.TECHNIQUE,
        },
        {
          label: this.dependencies.translateFn('Att&ck technique ID'),
          value: COMPLEX_SEARCH_FILTERS.TECHNIQUE_ID,
        },
        {
          label: this.dependencies.translateFn('Att&ck subtechnique'),
          value: COMPLEX_SEARCH_FILTERS.SUBTECHNIQUE,
        },
        {
          label: this.dependencies.translateFn('Att&ck subtechnique ID'),
          value: COMPLEX_SEARCH_FILTERS.SUBTECHNIQUE_ID,
        },
      );
    }

    filters.push(
      {
        label: this.dependencies.translateFn('IP'),
        value: COMPLEX_SEARCH_FILTERS.IP,
      },
      {
        label: this.dependencies.translateFn('MD5'),
        value: COMPLEX_SEARCH_FILTERS.MD5,
      },
      {
        label: this.dependencies.translateFn('SHA256'),
        value: COMPLEX_SEARCH_FILTERS.SHA256,
      },
      {
        label: this.dependencies.translateFn('Node name'),
        value: COMPLEX_SEARCH_FILTERS.NODE_NAME,
      },
      {
        label: this.dependencies.translateFn('Username'),
        value: COMPLEX_SEARCH_FILTERS.USER_NAME,
      },
      {
        label: this.dependencies.translateFn('File name'),
        value: COMPLEX_SEARCH_FILTERS.FILE_NAME,
      },
      {
        label: this.dependencies.translateFn('File path'),
        value: COMPLEX_SEARCH_FILTERS.FILE_PATH,
      },
      {
        label: this.dependencies.translateFn('URL'),
        value: COMPLEX_SEARCH_FILTERS.URL,
      },
    );

    if (Application.Main.hasFeatures(Feature.PRODUCT_FEATURE_EDR_INCIDENTS) && Application.Main.product.isCloud()) {
      filters.push(
        {
          label: this.dependencies.translateFn('MAC'),
          value: COMPLEX_SEARCH_FILTERS.MAC,
        },
        {
          label: this.dependencies.translateFn('Email subject'),
          value: COMPLEX_SEARCH_FILTERS.EMAIL_SUBJECT,
        },
        {
          label: this.dependencies.translateFn('Email address'),
          value: COMPLEX_SEARCH_FILTERS.EMAIL_ADDRESS,
        },
      );
    }

    filters.push(
      {
        label: this.dependencies.translateFn('Process name'),
        value: COMPLEX_SEARCH_FILTERS.PROCESS_NAME,
      },
      {
        label: this.dependencies.translateFn('Process path'),
        value: COMPLEX_SEARCH_FILTERS.PROCESS_PATH,
      },
      {
        label: this.dependencies.translateFn('Process PID'),
        value: COMPLEX_SEARCH_FILTERS.PROCESS_PID,
      },
      {
        label: this.dependencies.translateFn('Registry key'),
        value: COMPLEX_SEARCH_FILTERS.REGISTRY_KEY,
      },
    );

    if (
      Application.Main.hasAnyRights([Rights.RIGHT_MANAGE_INVENTORY]) &&
      Application.Main.hasFeatures(Feature.PRODUCT_FEATURE_EDR_CUSTOM_RULES) &&
      !Application.Main.hasFeatures(Feature.PRODUCT_FEATURE_CWS_STANDALONE)
    ) {
      filters.push({
        label: this.dependencies.translateFn('Detection Rule ID'),
        value: COMPLEX_SEARCH_FILTERS.DETECTION_RULES_ID,
      });
    }

    return filters;
  }

  translateActionTakenValues(value) {
    switch (value) {
      case ACTION_TAKEN.REPORTED:
        return this.dependencies.translateFn('Reported');
      case ACTION_TAKEN.BLOCKED:
        return this.dependencies.translateFn('Blocked');
      case ACTION_TAKEN.PARTIALLY_BLOCKED:
        return this.dependencies.translateFn('Partially blocked');
      default:
        return 'N/A';
    }
  }

  private getStatusValues() {
    return Object.values(STATUS)
      .filter(value => !isNaN(Number(value)))
      .map(value => {
        return {
          label: Translator.translateEdrIncidentStatus(value),
          value,
        };
      });
  }

  private getPhasrCategoriesValues() {
    const phasrCategories = Object.values(DetectedIncidentsTemplatesStringKeys)
      .map(value => {
        return {
          label: this.stringsTranslator.getPhasrCategoryString(value),
          value,
        };
      });

    return [
      {
        label: this.dependencies.translateFn('None'),
        value: 0,
      },
      ...phasrCategories,
    ];
  }

  private getPriorityValues() {
    return [
      {
        label: this.dependencies.translateFn('Critical'),
        value: PRIORITY.CRITICAL,
      },
      {
        label: this.dependencies.translateFn('High'),
        value: PRIORITY.HIGH,
      },
      {
        label: this.dependencies.translateFn('Medium'),
        value: PRIORITY.MEDIUM,
      },
      {
        label: this.dependencies.translateFn('Low'),
        value: PRIORITY.LOW,
      },
      {
        label: this.dependencies.translateFn('Unassigned'),
        value: PRIORITY.UNASSIGNED,
      },
    ];
  }

  protected getTypeValues() {
    return [
      {
        value: INCIDENT_TYPE.XDR,
        label: translateFn('Organization incident'),
      },
      {
        value: INCIDENT_TYPE.EDR,
        label: translateFn('Endpoint incident'),
      },
    ];
  }

  private getKillchainValues() {
    const killChainValues = Object.values(KILL_CHAIN_PHASES).map(item => {
      return {
        value: item,
        label: this.stringsTranslator.getKillChainPhasesTranslations(item),
      };
    });

    return [
      {
        label: this.dependencies.translateFn('None'),
        value: 0,
      },
      ...killChainValues,
    ];
  }

  public setGridViewProps(viewConfiguration) {
    this.dom.$grid.viewId = viewConfiguration.id;
    this.dom.$grid.columns = viewConfiguration.displayedColumns;

    this.dom.$grid.filters = viewConfiguration.filters;
    const defaultViewId = this.dom.$gridSmartViews.controller.view.smartViews.find(
      e => e.isReadOnly === true && e.category === 'default',
    )._id;
    const insertDemoIncident = viewConfiguration.id === defaultViewId;
    this.dom.$grid.extraParams = [{insertDemoIncident}];
    this.dom.$grid.isCompactViewChecked = viewConfiguration.compactView;
    this.dom.$grid.pageSize = viewConfiguration.pageSize;
    //column configs should be reset here otherwise they will not be overwritten
    this.dom.$grid.columnConfigs = [];

    this.addCustomColumnWidthToConfig(viewConfiguration.columnConfigs);

    this.dom.$grid.columnConfigs = viewConfiguration.columnConfigs ?? [];
    this.dom.$grid.favoriteIconConfig = GenericGridSmartViewsHelpers.configureFavoriteIcon(viewConfiguration);
  }

  /**
   * Method to apply custom widths to the columns from CUSTOM_COLUMN_WIDTH_CONFIGS
   *
   * @param columnConfigs
   * @private
   * @return void
   */
  private addCustomColumnWidthToConfig(columnConfigs: Array<GridColumnConfiguration>): void {
    if (columnConfigs) {
      columnConfigs.forEach(column => {
        if (Object.keys(CUSTOM_COLUMN_WIDTH_CONFIGS).includes(column.key)) {
          column.width = CUSTOM_COLUMN_WIDTH_CONFIGS[column.key].customWidth;
        }
      });
    }
  }

  handleNumberClick(incident) {
    this.dependencies.Ext.showIncidentView(incident);
  }

  handleCorrelatedClick(incident) {
    this.dependencies.Ext.showIncidentView(incident.id, true);
  }

  handleEntityIconClicked(iconType, rowData, gzIcon) {
    this.removeActiveStateFromGzIcon();
    gzIcon.active = true;
    const preProcessEntities = processEntitiesRowData(rowData, this.setEntityStringTranslate);
    const incidentsSideMenu = this.buildIncidentsSideMenuComponent(
      rowData._id,
      preProcessEntities,
      iconType,
      PanelType.ENTITIES_PANEL,
    );
    this.dom.$sideMenu.setMenuContent(incidentsSideMenu);
    this.dom.$sideMenu.openSideMenu();
  }

  handleResourceIconClicked(iconType, rowData, gzIcon) {
    this.removeActiveStateFromGzIcon();
    gzIcon.active = true;
    const processedResource = processResourcesRowData(rowData, this.setResourcesStringTranslate);
    const incidentsSideMenu = this.buildIncidentsSideMenuComponent(
      rowData._id,
      processedResource,
      iconType,
      PanelType.RESOURCES_PANEL,
    );
    this.dom.$sideMenu.setMenuContent(incidentsSideMenu);
    this.dom.$sideMenu.openSideMenu();
  }

  buildIncidentsSideMenuComponent(incidentId, categories, iconType, panelType) {
    const incidentsSideMenu = new IncidentsSideMenuWebComponent();
    incidentsSideMenu.categories = categories;
    incidentsSideMenu.incidentId = incidentId;

    if (panelType === PanelType.RESOURCES_PANEL) {
      const {
        title,
        searchCategoriesPlaceholder,
      } = this.stringsTranslator.getResourcesSidePanelStrings();
      incidentsSideMenu.title = title;
      incidentsSideMenu.searchCategoriesPlaceholder = searchCategoriesPlaceholder;
    }
    if (panelType === PanelType.ENTITIES_PANEL) {
      const {
        title,
        searchCategoriesPlaceholder,
      } = this.stringsTranslator.getEntitiesSidePanelStrings();
      incidentsSideMenu.title = title;
      incidentsSideMenu.searchCategoriesPlaceholder = searchCategoriesPlaceholder;
    }
    incidentsSideMenu.categoryToScroll = iconType;
    incidentsSideMenu.translations = this.stringsTranslator.getIncidentsSideMenuStrings();
    incidentsSideMenu.panelType = panelType;
    return incidentsSideMenu;
  }

  public removeActiveStateFromGzIcon() {
    this.dom.$grid.shadowRoot?.querySelectorAll(WEB_COMPONENT_GZ_ICON_LABEL).forEach(iconLabel => {
      iconLabel.active = false;
    });
  }

  private getDefaultFilters() {
    const filters: any = [
      {
        [ColumnKeys.NUMBER]: {
          eq: '',
        },
      },
      {
        [ColumnKeys.LAST_UPDATED]: {
          timeRange: {
            kind: TimeRangeKind.ALL_TO_DATE,
          },
        },
      },
      {
        [ColumnKeys.STATUS]: {
          in: this.getStatusValues().map(value => value.value),
        },
      },
      {
        [ColumnKeys.ASSIGNEE]: {
          value: null,
        },
      },
      {
        [ColumnKeys.SEARCH_DATA]: {
          complexSearch: {
            field: COMPLEX_SEARCH_FILTERS.ALERT_NAME,
            value: '',
          },
        },
      },
    ];

    if (!Application.Main.company.isCustomer()) {
      let defaultCompany = 'all';
      if (this.dependencies.getUser().mdrSuperAnalyst) {
        defaultCompany = this.dependencies.Ext.getActiveCompany().companyId;
      }
      filters.push({
        [ColumnKeys.COMPANY]: {
          in: [defaultCompany],
        },
      });
    }

    return filters;
  }

  public setEdrIncidentSideMenu(incident) {
    const edrIncidentDetailsWebComponent = new EdrIncidentDetailsWebComponent();
    edrIncidentDetailsWebComponent.incident = this.getIncidentDataForDetailsPanel(incident);

    edrIncidentDetailsWebComponent.translations = {
      incidentDetailsTitle: this.incidentsDetailsSidePanelString.incidentDetailsTitle,
      graphButtonText: this.incidentsDetailsSidePanelString.graphButtonTextForEdrIncidentPanel,
      eventsButtonText: this.incidentsDetailsSidePanelString.eventsButtonTextForEdrIncidentPanel,
      generalSectionLabels: this.getGeneralSectionLabels(),
      analysisSectionLabels: this.getAnalysisSectionLabels(),
      detectionsSectionLabels: this.getDetectionsSectionLabels(),
      attackInfoSectionLabels: this.getAttackInfoSectionLabels(true),
      techniquesSectionLabels: this.getTechniquesSectionLabels(),
    };

    this.dom.$sideMenu.setMenuContent(edrIncidentDetailsWebComponent);
    this.dom.$sideMenu.openSideMenu();
  }

  public setXdrIncidentsSideMenu(incident) {
    const xdrIncidentDetailsWebComponent = new XdrIncidentDetailsWebComponent();
    xdrIncidentDetailsWebComponent.translations = {
      generalSectionLabels: this.getGeneralSectionLabels(),
      attackInfoSectionLabels: this.getAttackInfoSectionLabels(),
      analysisSectionLabels: this.getAnalysisSectionLabels(),
      techniquesSectionLabels: this.getTechniquesSectionLabels(),
      incidentDetailsTitle: this.incidentsDetailsSidePanelString.incidentDetailsTitle,
      viewIncidentButton: this.dependencies.translateFn('View Incident'),
      viewResponsesButton: this.dependencies.translateFn('View Responses'),
    };
    xdrIncidentDetailsWebComponent.incident = this.getIncidentDataForDetailsPanel(incident);

    this.dom.$sideMenu.setMenuContent(xdrIncidentDetailsWebComponent);
    this.dom.$sideMenu.openSideMenu();
  }

  /**
   *
   * @param incident
   * @param {boolean} isEdrDetailsPanel
   * @returns {IncidentData}
   * @private
   */
  private getIncidentDataForDetailsPanel(incident): IncidentData {
    return {
      ...incident,
      extra: {
        assigneeName: this.dependencies.Ext.getAssigneeName(incident.assignee),
        actionTakenValue: this.translateActionTakenValues(incident.mainAction),
        isCustomer: Application.Main.company.isCustomer(),
      },
    };
  }

  private getAnalysisSectionLabels(): AnalysisSectionLabels {
    return {
      sectionTitle: this.incidentsDetailsSidePanelString.analysisSectionTitle,
      status: this.incidentsDetailsSidePanelString.status,
      assignee: this.incidentsDetailsSidePanelString.assignee,
      priority: this.incidentsDetailsSidePanelString.priority,
    };
  }

  private getDetectionsSectionLabels(): DetectionsSectionLabels {
    return {
      sectionTitle: this.incidentsDetailsSidePanelString.detectionsSectionTitle,
      incidentTrigger: this.incidentsDetailsSidePanelString.incidentTrigger,
      noResults: this.incidentsDetailsSidePanelString.noResults,
      showMoreBtn: this.incidentsDetailsSidePanelString.showMoreBtn,
    };
  }

  private getGeneralSectionLabels(): GeneralSectionLabels {
    return {
      sectionTitle: this.incidentsDetailsSidePanelString.generalSectionTitle,
      id: this.incidentsDetailsSidePanelString.id,
      created: this.incidentsDetailsSidePanelString.created,
      lastUpdated: this.incidentsDetailsSidePanelString.lastUpdated,
      company: this.incidentsDetailsSidePanelString.company,
      severity: this.incidentsDetailsSidePanelString.severity,
    };
  }

  private getAttackInfoSectionLabels(isEdrSection = false): AttackInfoSectionLabels {
    const attackInfoSectionLabels = {
      sectionTitle: this.incidentsDetailsSidePanelString.sectionTitle,
      actionTaken: this.incidentsDetailsSidePanelString.actionTaken,
      alerts: this.incidentsDetailsSidePanelString.alerts,
      entities: this.incidentsDetailsSidePanelString.entities,
      resources: this.incidentsDetailsSidePanelString.resources,
      correlatedIncidents: this.incidentsDetailsSidePanelString.correlatedIncidents,
      killChainPhases: this.incidentsDetailsSidePanelString.killChainPhases,
      showMoreBtn: this.incidentsDetailsSidePanelString.showMoreBtn,
      onEntityText: this.dependencies.translateFn('on#On device'),
      operatingSystem: this.incidentsDetailsSidePanelString.operatingSystem,
      artifacts: this.incidentsDetailsSidePanelString.artifacts,
      actorsTitle: this.incidentsDetailsSidePanelString.actorsTitle,
    };

    if (isEdrSection) {
      attackInfoSectionLabels.entities = this.incidentsDetailsSidePanelString.singleEntityLabel;
    }

    return attackInfoSectionLabels;
  }

  private getTechniquesSectionLabels(): TechniquesSectionLabels {
    return {
      sectionTitle: this.incidentsDetailsSidePanelString.techniquesSectionTitle,
    };
  }

  public setExtAlertPanelWidth() {
    const sidePanelWidth = this.dom.$sideMenu.shadowRoot?.querySelector('#sideMenu').style.width;
    const alertPanel = document.getElementsByClassName('ext-alert-side-menu-opened-from-webcomponets')[0]['style'];

    if (alertPanel) {
      alertPanel.setProperty('--side-panel-width', sidePanelWidth);
    }
  }

  getTimeOptionsForEdrStorageAddon() {
    return Application.Main.company.isPartner() ||
    Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_EDR_ES_365DAYS_STORAGE])
      ? computeTimeOptionsFromEdrStorageTimeRange('LAST_365_DAYS')
      : Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_EDR_ES_180DAYS_STORAGE])
        ? computeTimeOptionsFromEdrStorageTimeRange('LAST_180_DAYS')
        : computeTimeOptionsFromEdrStorageTimeRange('LAST_90_DAYS');
  }
}
